/**
 * Pure wallet-capability + init-error partitioning for the TRX PayPage input.
 *
 * Extracted so the "which wallet buttons can this browser actually mount" and
 * "which init errors may block the card form" decisions are unit-testable
 * without mounting React or the TRX library.
 *
 * Why this exists (2026-07-28 prod incident):
 *   The checkout mounted an Apple Pay button for EVERY TRX card session
 *   (`enableWallets` was on unconditionally), with no browser check. Apple Pay
 *   on the web is Safari-only — `window.ApplePaySession` does not exist in
 *   Chrome/Firefox — so the TRX library reported "Apple Pay is not supported on
 *   this browser" into `instance.errors`. The component then treated ANY
 *   non-empty `instance.errors` as fatal and showed "Please check your payment
 *   information and try again", breaking the (perfectly-mounted) card form for
 *   every non-Safari customer. An OPTIONAL wallet enhancement must never block
 *   the REQUIRED card path.
 *
 * Why it lives HERE rather than in `apps/checkout`, where it was written: TWO
 * components now mount TRX's wallet buttons — `apps/checkout`'s
 * `TrxJsLibraryInput` (customer checkout, mints its own session) and this
 * package's `<TrxCardFields>` (a guest paying at the table, handed a session
 * the server minted). A package cannot import an app, so leaving it there
 * meant a second copy of a rule whose FIRST copy already took the card form
 * down once.
 */

import type { TrxPayPageError } from "./trxPayPageTypes";
import { trxErrorMessage, trxErrorType } from "./trxPayPageTypes";

/**
 * The field names we hand the TRX library for the optional wallet buttons.
 * An init error tagged with one of these fields is wallet-only and must never
 * block the card form.
 */
export const TRX_WALLET_FIELD_NAMES = ["apple-pay", "google-pay"] as const;

/**
 * Minimal shape of the Apple Pay JS entry point we probe. Safari exposes
 * `window.ApplePaySession`; every other browser leaves it undefined.
 */
interface ApplePayCapableWindow {
  ApplePaySession?: {
    // `canMakePayments()` is the synchronous capability probe (does the
    // device have the Apple Pay hardware/OS support at all). We treat a
    // throwing or absent probe as "unavailable" — never as an error.
    canMakePayments?: () => boolean;
  };
}

/**
 * True only when the current browser can actually present an Apple Pay sheet:
 * `ApplePaySession` exists (Safari) AND `canMakePayments()` returns true.
 * Chrome/Firefox/Edge on any OS return false — which is correct, not an error.
 *
 * `win` is injectable for tests; defaults to the global `window` (or a
 * `{}`-like when there is no DOM, e.g. SSR/unit context) so a missing DOM reads
 * as "unavailable" rather than throwing.
 */
export function isApplePayAvailable(
  win: ApplePayCapableWindow | undefined = typeof window !== "undefined"
    ? (window as unknown as ApplePayCapableWindow)
    : undefined,
): boolean {
  const session = win?.ApplePaySession;
  if (!session || typeof session.canMakePayments !== "function") return false;
  try {
    return session.canMakePayments() === true;
  } catch {
    // A locked-down/permission-restricted Safari can throw here. Treat any
    // failure as "not available" — we simply don't offer the button.
    return false;
  }
}

/**
 * Split TRX library init errors into the ones that may BLOCK the card form and
 * the ones that are wallet-only (an optional Apple/Google Pay button failing to
 * mount). Wallet-only errors are logged, never surfaced as a card error.
 *
 * An error is wallet-only when its `field` is one of {@link TRX_WALLET_FIELD_NAMES}
 * (the library tags per-field errors) OR its `type`/`message` names a wallet —
 * the belt-and-suspenders match covers library builds that report the wallet
 * failure without a `field`.
 */
export function partitionTrxInitErrors(
  errors: readonly TrxPayPageError[] | undefined,
): { blocking: TrxPayPageError[]; walletOnly: TrxPayPageError[] } {
  const blocking: TrxPayPageError[] = [];
  const walletOnly: TrxPayPageError[] = [];
  for (const err of errors ?? []) {
    if (isWalletError(err)) {
      walletOnly.push(err);
    } else {
      blocking.push(err);
    }
  }
  return { blocking, walletOnly };
}

function isWalletError(err: TrxPayPageError): boolean {
  const field = err.field?.toLowerCase() ?? "";
  if ((TRX_WALLET_FIELD_NAMES as readonly string[]).includes(field)) return true;
  // Both spellings — the live bundle emits errorType/errorText, so reading
  // only type/message matched nothing and every wallet error was treated as
  // blocking (prod 2026-07-30).
  const haystack = `${trxErrorType(err)} ${trxErrorMessage(err)}`.toLowerCase();
  return (
    haystack.includes("apple pay") ||
    haystack.includes("applepay") ||
    haystack.includes("apple-pay") ||
    haystack.includes("google pay") ||
    haystack.includes("googlepay") ||
    haystack.includes("google-pay")
  );
}
